import axios, { AxiosError } from "axios";

// Axios client wired for Laravel Sanctum SPA auth.
// In dev: Vite proxies /api and /sanctum to http://localhost:8000.
// In prod: VITE_API_BASE_URL points at https://api.lekkiservicedflats.com.

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

/**
 * Fetch the XSRF-TOKEN cookie before any state-mutating request.
 * Must be called before login (and at app boot to refresh after CSRF expiry).
 */
export async function getCsrfCookie(): Promise<void> {
  await api.get("/sanctum/csrf-cookie");
}

// ─── Auth-state event bus ────────────────────────────────────────────────────
// The interceptors below dispatch typed events that AuthProvider listens for.
// This keeps axios free of React imports and avoids circular dependencies.

export type AuthEvent =
  | { type: "unauthenticated" }
  | { type: "password_change_required" }
  | { type: "session_expired" };

type Listener = (event: AuthEvent) => void;
const listeners = new Set<Listener>();

export function onAuthEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(event: AuthEvent): void {
  for (const listener of listeners) listener(event);
}

// ─── Response interceptor ────────────────────────────────────────────────────
let csrfRetryInFlight: Promise<void> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config;

    // 419 — CSRF token expired. Refresh the XSRF cookie once and retry.
    if (status === 419 && original && !(original as { _csrfRetried?: boolean })._csrfRetried) {
      (original as { _csrfRetried?: boolean })._csrfRetried = true;
      csrfRetryInFlight ??= getCsrfCookie().finally(() => {
        csrfRetryInFlight = null;
      });
      await csrfRetryInFlight;
      return api.request(original);
    }

    // 423 — password change required. AuthProvider routes the user.
    if (status === 423) {
      emit({ type: "password_change_required" });
    }

    // 401 — session ended or never had one. AuthProvider clears local state.
    // A session that idled out carries a distinguishing code so the user sees
    // "your session expired" instead of silently landing back on /login.
    if (status === 401) {
      const code = (error.response?.data as { code?: string } | undefined)?.code;
      emit(code === "session_idle_timeout" ? { type: "session_expired" } : { type: "unauthenticated" });
    }

    return Promise.reject(error);
  },
);
